/**
 * useAssistantChat — SSOT for the assistant-tab lifecycle.
 *
 * Wraps `useStreamingChat` and owns every piece of state App.tsx used
 * to juggle inline: loading spinner for the historical-messages fetch,
 * the race guard around "just-created chat — don't re-fetch it", and
 * the cross-module bus emits on chat create / move / touch.
 *
 * Consumers (App.tsx) pass the selected chat/project ids in as
 * controlled props (navigation is its own SSOT via `useNavigationState`)
 * and get back the values and callbacks to feed into ChatRoom.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { emit as emitAppEvent } from '../../core/events/bus';
import { useToastContext } from '../../core/ui/toast/ToastProvider';
import { getMessagesForChat, moveChatToProject } from './chatservice';
import { createChat } from './chatMetadataService';
import { useStreamingChat } from './useStreamingChat';

export type UseAssistantChatArgs = {
    selectedChatId: string | null;
    selectedProjectId: string | null;
    setSelectedChatId: (id: string | null) => void;
    setSelectedProjectId: (id: string | null) => void;
    /** Called when the user selects a chat; used by the host to also
     *  swap the active tab to `'assistant'`. */
    onChatActivated: () => void;
};

export function useAssistantChat(args: UseAssistantChatArgs) {
    const {
        selectedChatId, selectedProjectId,
        setSelectedChatId, setSelectedProjectId,
        onChatActivated,
    } = args;
    const { addToast } = useToastContext();

    const [loadingMessages, setLoadingMessages] = useState(false);
    const skipLoadForChatIdRef = useRef<string | null>(null);

    // Streaming hook — SSOT for the assistant-turn state machine.
    const {
        messages,
        setMessages,
        send: sendStream,
        stop: stopStream,
        regenerate: regenerateStream,
        state: chatState,
        lastError: chatLastError,
    } = useStreamingChat({
        chatId: selectedChatId,
        onUsageRefresh: () => emitAppEvent('user-invalidate', { reason: 'profile-save' }),
        // quota_warning intentionally doesn't toast — the persistent
        // ChatQuotaBanner is authoritative. A parallel toast would
        // collide with the banner's Upgrade CTA.
        onQuotaExhausted: () => {
            addToast(
                "You've used all your monthly tokens. Upgrade to keep chatting.",
                'error',
            );
            emitAppEvent('open-plans', { reason: 'quota' });
        },
    });

    // Surface transient stream errors via the toast system.
    useEffect(() => {
        if (chatLastError) {
            addToast(chatLastError.message || 'Chat error. Please try again.', 'error');
        }
    }, [chatLastError, addToast]);

    // Load historical messages when a chat is selected. Skips the
    // refetch when `skipLoadForChatIdRef` matches — set after we just
    // created + streamed a fresh chat, so the server-side history
    // request doesn't race the streaming replacement.
    useEffect(() => {
        if (!selectedChatId) {
            setMessages([]);
            setLoadingMessages(false);
            return;
        }
        if (skipLoadForChatIdRef.current === selectedChatId) {
            skipLoadForChatIdRef.current = null;
            return;
        }

        let cancelled = false;
        async function loadMessages() {
            setLoadingMessages(true);
            try {
                const chatMessages = await getMessagesForChat(selectedChatId!);
                if (!cancelled) setMessages(chatMessages);
            } catch (error) {
                if (!cancelled) console.error('Failed to load messages:', error);
            } finally {
                if (!cancelled) setLoadingMessages(false);
            }
        }
        loadMessages();
        return () => { cancelled = true; };
    }, [selectedChatId, setMessages]);

    const sendMessage = useCallback(async (content: string, file?: File | null) => {
        try {
            let chatId = selectedChatId;

            if (!chatId && selectedProjectId) {
                const created = await createChat(selectedProjectId, null, content);
                chatId = created.id;
                skipLoadForChatIdRef.current = chatId;
                setSelectedChatId(chatId);
                emitAppEvent('project-chat-created', {
                    projectId: selectedProjectId,
                    chat: { id: created.id, name: created.name },
                });
            } else if (!chatId) {
                const created = await createChat(null, null, content);
                chatId = created.id;
                setSelectedProjectId(null);
                skipLoadForChatIdRef.current = chatId;
                setSelectedChatId(chatId);
                emitAppEvent('direct-chat-created', { id: created.id, name: created.name });
            }

            if (chatId && selectedProjectId) {
                emitAppEvent('project-chat-touched', { projectId: selectedProjectId, chatId });
            } else if (chatId && selectedProjectId === null) {
                emitAppEvent('direct-chat-touched', { chatId });
            }

            await sendStream(content, file ?? null, { chatId });
        } catch (error) {
            console.error('Failed to send message:', error);
            addToast('Failed to send message. Please try again.', 'error');
        }
    }, [selectedChatId, selectedProjectId, setSelectedChatId, setSelectedProjectId, sendStream, addToast]);

    const newDirectChat = useCallback(() => {
        setSelectedChatId(null);
        setSelectedProjectId(null);
        setMessages([]);
        setLoadingMessages(false);
    }, [setSelectedChatId, setSelectedProjectId, setMessages]);

    const moveToProject = useCallback(async (targetProjectId: string | null) => {
        if (!selectedChatId) {
            setSelectedProjectId(targetProjectId);
            return;
        }
        try {
            await moveChatToProject(selectedChatId, targetProjectId);
            setSelectedProjectId(targetProjectId);
            emitAppEvent('chat-moved', {
                chatId: selectedChatId,
                fromProjectId: selectedProjectId,
                toProjectId: targetProjectId,
            });
        } catch (error) {
            console.error('Failed to move chat:', error);
            addToast('Failed to move chat to project', 'error');
        }
    }, [selectedChatId, selectedProjectId, setSelectedProjectId, addToast]);

    const createProjectFromChat = useCallback(() => {
        emitAppEvent('open-create-project-modal', { chatId: selectedChatId });
    }, [selectedChatId]);

    const selectChat = useCallback((projectId: string, chatId: string) => {
        const normalizedProjectId = projectId ? projectId : null;
        setSelectedProjectId(normalizedProjectId);
        onChatActivated();

        if (!chatId) {
            setSelectedChatId(null);
            setMessages([]);
            setLoadingMessages(false);
            return;
        }
        if (chatId === selectedChatId) return;

        setMessages([]);
        setLoadingMessages(true);
        setSelectedChatId(chatId);
    }, [selectedChatId, setSelectedChatId, setSelectedProjectId, setMessages, onChatActivated]);

    return {
        messages,
        chatState,
        loadingMessages,
        sendMessage,
        stopStream,
        regenerateStream,
        newDirectChat,
        moveToProject,
        createProjectFromChat,
        selectChat,
    };
}
